import type Highcharts from "highcharts";

export type TierStyle = {
  bg: string;
  border: string;
  text: string;
  dot: string;
};

// Tier palette is shared by every chart (scatter, map, lists, badges) so a
// practice keeps one color everywhere. Dots use the brand segment colors
// (periwinkle / lemon / tangerine / gray); bg/border/text are tints and
// shades of the same hue tuned for badge contrast. Tiers always carry a
// text badge, never color alone.
export const TIER_STYLES: Record<string, TierStyle> = {
  Performing: {
    bg: "#E6E7F4",
    border: "#ABAFD6",
    text: "#3A3F7A",
    dot: "#565CA8",
  },
  Steady: {
    bg: "#F6FBD9",
    border: "#C5D64B",
    text: "#5C660F",
    dot: "#E2F462",
  },
  "Priority Focus": {
    bg: "#FCE5DA",
    border: "#F5AE8C",
    text: "#963F14",
    dot: "#EE7340",
  },
  External: {
    bg: "#F1F2F4",
    border: "#C8CCD2",
    text: "#4A5058",
    dot: "#7B828C",
  },
};

export const DEFAULT_TIER_STYLE: TierStyle = {
  bg: "#f1f5f9",
  border: "#cbd5e1",
  text: "#475569",
  dot: "#94a3b8",
};

export function getTierStyle(tier?: string): TierStyle & { headerBg: string } {
  const headerBgMap: Record<string, string> = {
    Performing: "#F2F3FA",
    Steady: "#FBFDEC",
    "Priority Focus": "#FEF3EE",
    External: "#F8F9FA",
  };
  const base = (tier ? TIER_STYLES[tier] : undefined) ?? DEFAULT_TIER_STYLE;
  return {
    ...base,
    headerBg: (tier ? headerBgMap[tier] : undefined) ?? "#f8fafc",
  };
}

export type ChartRendererFn = (
  data: unknown,
  spec: unknown,
  title: string,
  onPointClick?: (point: unknown) => void,
) => Highcharts.Options | null;

export type TableColumnDef = {
  key: string;
  header: string;
  align?: "start" | "center" | "end";
  cellType?: "tier-badge";
};

export type ChartRegistryEntry = {
  renderer: ChartRendererFn;
  tableColumns?: TableColumnDef[];
  getTableRows?: (data: unknown) => Array<Record<string, unknown>>;
  fillHeight?: boolean;
  constructorType?: string;
  exportItems?: string[];
};

// ToggleableChart extends a Highcharts chart instance with the two methods
// that context-menu onclick handlers call via `this` (the chart).
// These are attached in ChartFactory's callback and ChartTableView's callback.
export type ToggleableChart = Highcharts.Chart & {
  setChartType?: (chartType: string) => void;
  convertToSourceChart?: () => void;
};

export type BarChartRow = { name: string; value: number; percentage?: number };
export type LineTableRow = { series: string; category: string; value: number };
export type MapTableRow = { state: string; cases: number };

export type TieredBarTableRow = {
  prescriber: string;
  pts: number;
  pa_percentage: string;
  abandon_percentage: string;
  tier: string;
};

export type QuadrantScatterTableRow = {
  practice: string;
  pts: number;
  pa_cases: number;
  pa_approved_cases: number;
  pa_percentage: string;
  abandon_percentage: string;
  tier: string;
};

export type GenericTableData = {
  headers: string[];
  rows: Array<Record<string, unknown>>;
};

export type DrilldownRow = {
  prescriber: string;
  doctor_id: string;
  pts: number | string;
  pa_cases: number | string;
  pa_approved_cases: number | string;
  pa_abandoned_cases: number | string;
  pa_percentage: string;
  abandon_percentage: string;
  tier: string;
};

export type PracticeDrilldown = {
  type: string;
  count: number;
  limit: number;
  rows: DrilldownRow[];
};

export type QuadrantPractice = {
  practice?: string;
  practice_id?: number;
  location?: string;
  pts?: number | string;
  pa_cases?: number | string;
  pa_approved_cases?: number | string;
  pa_abandoned_cases?: number | string;
  pa_percentage?: string;
  abandon_percentage?: string;
  tier?: string;
  drilldown?: PracticeDrilldown;
};

export type FunnelStage = {
  name: string;
  description?: string;
  count: number;
  percentage: number;
  color?: string;
  order?: number;
  breakdown?: Record<string, unknown>;
  drilldown?: Record<string, unknown>;
};
